/**
 * Admin → Audit log. Append-only record of admin HTTP mutations,
 * written by the audit middleware after each /v1/admin/* state-change.
 *
 * Reseller-scoped on the server: each reseller / platform admin sees
 * only their own activity. No cross-tenant audit endpoint exists yet.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface AuditEntry {
  id: number;
  actor_user_id?: number;
  actor_email: string;
  actor_tenant_id?: number;
  actor_tenant_type?: string;
  method: string;
  path: string;
  /** Already scrubbed JSON; the middleware redacts password / auth_token / etc. */
  request_body?: unknown;
  status_code: number;
  duration_ms: number;
  request_id?: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface AuditListResponse {
  items: AuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditListFilter {
  method?: string;
  path?: string;
  email?: string;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}

const AUDIT_KEY = ['admin', 'audit'] as const;

export function useAuditLog(filter: AuditListFilter = {}) {
  const params = new URLSearchParams();
  if (filter.method) params.set('method', filter.method);
  if (filter.path) params.set('path', filter.path);
  if (filter.email) params.set('email', filter.email);
  if (filter.since) params.set('since', filter.since);
  if (filter.until) params.set('until', filter.until);
  if (filter.limit !== undefined) params.set('limit', String(filter.limit));
  if (filter.offset !== undefined) params.set('offset', String(filter.offset));
  const qs = params.toString();
  return useQuery({
    queryKey: [...AUDIT_KEY, qs] as const,
    queryFn: () => api.get<AuditListResponse>(`/v1/admin/audit${qs ? `?${qs}` : ''}`),
    // Audit is append-only; cache for a minute so paging and filter
    // tweaks don't refetch on every keystroke. The user can hard-reload
    // the page if they want fresh data.
    staleTime: 60_000,
  });
}
